/**
 * Zustand Notification Store (AC-538)
 *
 * Manages notification state including:
 * - Notification list with pagination
 * - Unread count for badge
 * - Polling for new notifications
 * - Mark as read projects
 *
 * Related Tickets:
 * - AC-532 (Epic: In-App Notification System)
 * - AC-538 (Notification store - THIS TICKET)
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import apiService from '../services/api';
import {
  Notification,
  NotificationQuery,
  NotificationState,
  NotificationActions,
  NotificationStore,
} from '../types/notification';

// Default pagination limit
const DEFAULT_LIMIT = 20;

// Initial state
const initialState: NotificationState & { isDropdownOpen: boolean } = {
  notifications: [],
  unreadCount: 0,
  totalCount: 0,
  hasMore: false,
  isLoading: false,
  error: null,
  isPollingEnabled: false,
  lastPollTime: null,
  lastNotificationId: null,
  isDropdownOpen: false,
};

/**
 * Notification store for managing in-app notifications.
 */
export const useNotificationStore = create<NotificationStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      ...initialState,

      // =======================================================================
      // FETCH NOTIFICATIONS
      // =======================================================================
      fetchNotifications: async (query?: NotificationQuery) => {
        set({ isLoading: true, error: null });

        try {
          const params = {
            skip: query?.skip ?? 0,
            limit: query?.limit ?? DEFAULT_LIMIT,
            is_read: query?.is_read,
            notification_type: query?.notification_type,
            priority: query?.priority,
          };

          const response = await apiService.getNotifications(params);

          set({
            notifications: response.notifications as Notification[],
            unreadCount: response.unread_count,
            totalCount: response.total_count,
            hasMore: response.has_more,
            isLoading: false,
            // Track last notification ID for polling
            lastNotificationId:
              response.notifications.length > 0
                ? response.notifications[0].notification_id
                : get().lastNotificationId,
          });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Failed to fetch notifications';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // =======================================================================
      // LOAD MORE (Pagination)
      // =======================================================================
      loadMore: async () => {
        const { notifications, hasMore, isLoading } = get();

        if (!hasMore || isLoading) {
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const response = await apiService.getNotifications({
            skip: notifications.length,
            limit: DEFAULT_LIMIT,
          });

          set((state) => ({
            notifications: [...state.notifications, ...(response.notifications as Notification[])],
            unreadCount: response.unread_count,
            totalCount: response.total_count,
            hasMore: response.has_more,
            isLoading: false,
          }));
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Failed to load more notifications';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // =======================================================================
      // POLL FOR NEW NOTIFICATIONS
      // =======================================================================
      pollNotifications: async () => {
        const { lastPollTime, lastNotificationId } = get();

        try {
          const params: { since?: string; last_id?: number } = {};
          if (lastPollTime) {
            params.since = lastPollTime;
          }
          if (lastNotificationId) {
            params.last_id = lastNotificationId;
          }

          const response = await apiService.pollNotifications(params);

          if (response.notifications.length > 0) {
            set((state) => {
              // Add new notifications at the beginning
              const existingIds = new Set(state.notifications.map((n) => n.notification_id));
              const newNotifications = (response.notifications as Notification[]).filter(
                (n) => !existingIds.has(n.notification_id)
              );

              return {
                notifications: [...newNotifications, ...state.notifications],
                unreadCount: response.unread_count,
                lastPollTime: response.server_time,
                lastNotificationId:
                  newNotifications.length > 0
                    ? newNotifications[0].notification_id
                    : state.lastNotificationId,
              };
            });
          } else {
            // Update poll time even if no new notifications
            set({
              unreadCount: response.unread_count,
              lastPollTime: response.server_time,
            });
          }
        } catch (error) {
          // Don't set error for polling failures - they're background projects
          console.error('Polling failed:', error);
        }
      },

      // =======================================================================
      // MARK AS READ
      // =======================================================================
      markAsRead: async (notificationId: number) => {
        try {
          await apiService.markNotificationRead(notificationId);

          set((state) => ({
            notifications: state.notifications.map((n) =>
              n.notification_id === notificationId
                ? { ...n, is_read: true, read_at: new Date().toISOString() }
                : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          }));
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Failed to mark notification as read';
          set({ error: message });
          throw error;
        }
      },

      // =======================================================================
      // MARK ALL AS READ
      // =======================================================================
      markAllAsRead: async () => {
        try {
          await apiService.markAllNotificationsRead();

          set((state) => ({
            notifications: state.notifications.map((n) => ({
              ...n,
              is_read: true,
              read_at: n.read_at || new Date().toISOString(),
            })),
            unreadCount: 0,
          }));
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Failed to mark all notifications as read';
          set({ error: message });
          throw error;
        }
      },

      // =======================================================================
      // DELETE NOTIFICATION
      // =======================================================================
      deleteNotification: async (notificationId: number) => {
        try {
          // Optimistically update UI
          const notificationToDelete = get().notifications.find(
            (n) => n.notification_id === notificationId
          );
          const wasUnread = notificationToDelete && !notificationToDelete.is_read;

          set((state) => ({
            notifications: state.notifications.filter(
              (n) => n.notification_id !== notificationId
            ),
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
            totalCount: Math.max(0, state.totalCount - 1),
          }));

          // Make API call
          await apiService.deleteNotification(notificationId);
        } catch (error) {
          // Revert on failure - refetch to get accurate state
          await get().fetchNotifications();
          const message =
            error instanceof Error ? error.message : 'Failed to delete notification';
          set({ error: message });
          throw error;
        }
      },

      // =======================================================================
      // FETCH UNREAD COUNT
      // =======================================================================
      fetchUnreadCount: async () => {
        try {
          const response = await apiService.getUnreadNotificationCount();
          set({ unreadCount: response.unread_count });
        } catch (error) {
          // Don't set error for count fetches - non-critical project
          console.error('Failed to fetch unread count:', error);
        }
      },

      // =======================================================================
      // POLLING CONTROL
      // =======================================================================
      setPollingEnabled: (enabled: boolean) => {
        set({ isPollingEnabled: enabled });
      },

      // =======================================================================
      // ERROR HANDLING
      // =======================================================================
      clearError: () => {
        set({ error: null });
      },

      // =======================================================================
      // RESET
      // =======================================================================
      reset: () => {
        set(initialState);
      },

      // =======================================================================
      // DROPDOWN CONTROL
      // =======================================================================
      isDropdownOpen: false,

      setDropdownOpen: (open: boolean) => {
        set({ isDropdownOpen: open });
      },

      // =======================================================================
      // TRIGGER IMMEDIATE POLL
      // =======================================================================
      triggerImmediatePoll: async () => {
        // Fetch latest notifications immediately
        try {
          const response = await apiService.getNotifications({
            skip: 0,
            limit: DEFAULT_LIMIT,
          });

          set({
            notifications: response.notifications as Notification[],
            unreadCount: response.unread_count,
            totalCount: response.total_count,
            hasMore: response.has_more,
            lastNotificationId:
              response.notifications.length > 0
                ? response.notifications[0].notification_id
                : null,
          });
        } catch (error) {
          console.error('Immediate poll failed:', error);
        }
      },
    }),
    { name: 'notification-store' }
  )
);

// Export selectors for optimized component subscriptions
export const selectNotifications = (state: NotificationStore) => state.notifications;
export const selectUnreadCount = (state: NotificationStore) => state.unreadCount;
export const selectIsLoading = (state: NotificationStore) => state.isLoading;
export const selectError = (state: NotificationStore) => state.error;
export const selectHasMore = (state: NotificationStore) => state.hasMore;
export const selectIsPollingEnabled = (state: NotificationStore) => state.isPollingEnabled;

export default useNotificationStore;
